import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, AlertCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public page reached from the "Reset vault passcode" email.
 *
 * Flow:
 *  1. On mount, pre-flight check the token at `/vault/passcode/reset/check`.
 *     Lets us show a *specific* error (expired / already-used / superseded)
 *     BEFORE the owner types a new passcode and hits a generic 400.
 *  2. If the token is valid, render the new-passcode form.
 *  3. On submit, POST `/vault/passcode/reset` + drop the new vault session
 *     token into sessionStorage so the dashboard is already unlocked.
 */
export default function VaultReset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [check, setCheck] = useState({ status: "checking", message: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/vault/passcode/reset/check`, { params: { token } });
        if (cancelled) return;
        if (r.data?.ok) {
          setCheck({ status: "valid", emailHint: r.data.email_hint || "" });
        } else {
          setCheck({ status: "invalid", reason: r.data?.reason || "unknown", message: r.data?.message || "This reset link is no longer valid." });
        }
      } catch (e) {
        if (!cancelled) setCheck({ status: "invalid", reason: "network", message: "Couldn't reach ChildCheckOh. Check your connection and try again." });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const np = newPass.trim();
    if (np.length < 4 || np.length > 32) return toast.error("Passcode must be 4–32 characters.");
    if (np !== confirmPass.trim()) return toast.error("Passcodes don't match.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/vault/passcode/reset`, {
        token, new_passcode: np,
      });
      sessionStorage.setItem("cc_vault_token", r.data.token);
      setDone(true);
      toast.success("Vault passcode reset.");
      setTimeout(() => navigate("/dashboard/staff/documents"), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not reset passcode.";
      toast.error(typeof detail === "string" ? detail : "Could not reset passcode.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Shell title="Passcode reset!" icon={ShieldCheck} tone="emerald"
        description="Taking you to your dashboard…">
        <div className="grid place-items-center py-2">
          <Loader2 className="w-5 h-5 animate-spin text-emerald-700" />
        </div>
      </Shell>
    );
  }

  if (check.status === "checking") {
    return (
      <Shell title="Checking your reset link" icon={KeyRound}
        description="Just a moment — confirming the link is still valid.">
        <div className="grid place-items-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-emerald-700" />
        </div>
      </Shell>
    );
  }

  if (check.status === "invalid") {
    const reasonLabel = {
      expired:        "Link expired",
      used_or_invalid:"Link no longer valid",
      missing_token:  "Link broken",
      network:        "Connection problem",
    }[check.reason] || "Link no longer valid";
    return (
      <Shell title={reasonLabel} icon={AlertTriangle} tone="amber"
        description={check.message}>
        <div className="space-y-3" data-testid="vault-reset-invalid">
          <Link to="/dashboard/staff/documents" data-testid="vault-reset-back"
            className="block text-center bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11 leading-[44px] font-medium">
            Open the dashboard
          </Link>
          <p className="text-[11px] text-stone-500 text-center leading-relaxed">
            From the dashboard, click any vault section (Records, Staff Docs, Licensing, Compliance, Billing)
            → tap <b>"Forgot your passcode? Email me a reset link"</b>. The new email overrides this one.
          </p>
        </div>
      </Shell>
    );
  }

  // status === "valid"
  return (
    <Shell title="Choose a new vault passcode" icon={KeyRound}
      description={`One-time reset link${check.emailHint ? ` for ${check.emailHint}` : ""}. Pick a new 4–32 character passcode for Records, Staff Docs, Licensing, Compliance, and Billing.`}>
      <form onSubmit={submit} className="space-y-3" data-testid="vault-reset-form">
        <Field label="New passcode" value={newPass} onChange={setNewPass} testid="vault-reset-new" autoFocus />
        <Field label="Confirm passcode" value={confirmPass} onChange={setConfirmPass} testid="vault-reset-confirm" />
        <Button type="submit" disabled={busy} data-testid="vault-reset-submit"
          className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting…</>
                : <><ShieldCheck className="w-4 h-4 mr-2" /> Reset passcode &amp; unlock</>}
        </Button>
      </form>
      <p className="mt-4 text-[11px] text-stone-500">
        Link expired or didn't work? <Link to="/dashboard/staff/documents" className="text-emerald-700 hover:underline">Open the dashboard</Link> and click "Forgot your passcode?" again to get a fresh link.
      </p>
    </Shell>
  );
}

function Shell({ title, description, icon: Icon, children, tone = "amber" }) {
  const accent = tone === "amber" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  return (
    <div className="min-h-screen grid place-items-center px-4 bg-stone-50" data-testid="vault-reset-shell">
      <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white shadow-sm p-7">
        <div className={`w-12 h-12 rounded-xl ${accent} grid place-items-center mb-5`}>
          <Icon className="w-6 h-6" />
        </div>
        <h1 className="font-display text-2xl font-medium text-stone-900">{title}</h1>
        <p className="mt-2 text-sm text-stone-600 leading-relaxed">{description}</p>
        <div className="mt-6">{children}</div>
        <div className="mt-6 flex items-center gap-2 text-[11px] text-stone-400">
          <AlertCircle className="w-3.5 h-3.5" /> Single-use link · expires 15 min after request
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, autoFocus }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-stone-700">{label}</Label>
      <Input type="password" inputMode="numeric" autoComplete="off" autoFocus={autoFocus}
        data-testid={testid}
        value={value} onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-xl border-stone-300 focus-visible:ring-emerald-700 text-base tracking-widest" />
    </div>
  );
}
